import { P2GAlert, P2GBox, P2GButton, P2GGrid, P2GIcon, P2GText, SvgParcel } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { AppCard } from '~/components/card/app-card';
import { SettingsCache } from '~/lib/consts/settings-cache';
import useGoto from '~/hooks/use-goto';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { TranslationNamespaces } from '~/lib/constants';
import { SidePanelSections } from '~/components/sidepanel-section';
import { buildQuickSendSteps } from './QuickSendSteps';

interface QuickSendEntryNewProps {
  onCreateShipment: () => void;
  onCsvUpload: () => void;
  onRestore: () => void;
}

const QuickSendEntryNew = ({ onCreateShipment, onCsvUpload, onRestore }: QuickSendEntryNewProps) => {
  const { goTo } = useGoto();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const hasCsvUpload = SettingsCache.settings.Features.includes('CsvUpload');
  const hasRestoreItem = SettingsCache.settings.Features.includes('RestoreItem');
  const cardCount = 1 + (hasCsvUpload ? 1 : 0) + (hasRestoreItem ? 1 : 0);
  const cardSize = 12 / cardCount;
  const steps = buildQuickSendSteps(t);

  return (
    <SidePanelSections>
      <P2GText component="p" variant="body2">
        {t('QUICK_SEND_ENTRY_NEW_INTRO', { defaultValue: "Let's get your first parcel on its way" })}
      </P2GText>

      <P2GBox sx={{ textAlign: 'center' }}>
        <P2GBox mb={2} sx={{ display: 'flex', justifyContent: 'center' }}>
          <P2GBox
            p={2}
            sx={(theme) => ({
              backgroundColor: theme.palette.info.light,
              borderRadius: '50%',
              width: 64,
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            })}
          >
            <P2GIcon size="lg">
              <SvgParcel />
            </P2GIcon>
          </P2GBox>
        </P2GBox>
        <P2GText variant="h6">
          <strong>{t('QUICK_SEND_ENTRY_NEW_HEADING', { defaultValue: 'Ready to ship?' })}</strong>
        </P2GText>
        <P2GText component="p" variant="body2" color="text.secondary">
          {t('QUICK_SEND_ENTRY_NEW_SUBHEADING', { defaultValue: 'Create your first order in under a minute' })}
        </P2GText>
        <P2GBox mt={2}>
          <P2GButton variant="primary" fullWidth onClick={onCreateShipment}>
            {t('CREATE_NEW_ORDER_BUTTON', { defaultValue: 'Create New Order' })}
          </P2GButton>
        </P2GBox>
      </P2GBox>

      <P2GBox>
        <P2GText component="p" variant="body2" color="textSecondary">
          <strong>{t('HOW_IT_WORKS_LABEL', { defaultValue: 'HOW IT WORKS' })}</strong>
        </P2GText>
        <P2GBox mt={1} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {steps.map((step) => (
            <P2GGrid key={step.label} container spacing={1} alignItems="center" justify="flex-start" wrap="nowrap">
              <P2GGrid item>
                <P2GIcon size="xs">{step.icon}</P2GIcon>
              </P2GGrid>
              <P2GGrid item>
                <P2GText variant="body2">
                  <strong>{step.label}</strong>
                </P2GText>
                <P2GText component="p" variant="caption">
                  {step.detail}
                </P2GText>
              </P2GGrid>
            </P2GGrid>
          ))}
        </P2GBox>
      </P2GBox>

      <P2GGrid container spacing={2}>
        {hasCsvUpload && (
          <P2GGrid item size={{ xs: cardSize }}>
            <P2GBox onClick={onCsvUpload} sx={{ cursor: 'pointer', height: '100%' }}>
              <AppCard>
                <P2GBox p={2} sx={{ textAlign: 'center' }}>
                  <P2GText variant="body2">
                    <strong>{t('ADD_TOOLTIP_UPLOAD_CSV', { defaultValue: 'Upload CSV' })}</strong>
                  </P2GText>
                  <P2GText component="p" variant="caption" color="text.secondary">
                    {t('BULK_IMPORT_LABEL', { defaultValue: 'Bulk import' })}
                  </P2GText>
                </P2GBox>
              </AppCard>
            </P2GBox>
          </P2GGrid>
        )}
        <P2GGrid item size={{ xs: cardSize }}>
          <P2GBox onClick={() => goTo(RoutingConstants.AccountLinking)} sx={{ cursor: 'pointer', height: '100%' }}>
            <AppCard>
              <P2GBox p={2} sx={{ textAlign: 'center' }}>
                <P2GText variant="body2">
                  <strong>{t('CONNECT_STORE_LABEL', { defaultValue: 'Connect Store' })}</strong>
                </P2GText>
                <P2GText component="p" variant="caption" color="text.secondary">
                  {t('AUTO_SYNC_ORDERS_LABEL', { defaultValue: 'Auto-sync orders' })}
                </P2GText>
              </P2GBox>
            </AppCard>
          </P2GBox>
        </P2GGrid>
        {hasRestoreItem && (
          <P2GGrid item size={{ xs: cardSize }}>
            <P2GBox onClick={onRestore} sx={{ cursor: 'pointer', height: '100%' }}>
              <AppCard>
                <P2GBox p={2} sx={{ textAlign: 'center' }}>
                  <P2GText variant="body2">
                    <strong>{t('ADD_TOOLTIP_RESTORE_ITEM', { defaultValue: 'Restore Deleted' })}</strong>
                  </P2GText>
                  <P2GText component="p" variant="caption" color="text.secondary">
                    {t('RESTORE_ITEM_CAPTION', { defaultValue: 'Recover deleted orders' })}
                  </P2GText>
                </P2GBox>
              </AppCard>
            </P2GBox>
          </P2GGrid>
        )}
      </P2GGrid>

      <P2GAlert type="warning">
        <P2GText component="p" variant="body2" color="warning">
          <strong>{t('PRO_TIP_LABEL', { defaultValue: 'Pro tip' })}</strong>
        </P2GText>
        <P2GText component="p" variant="body2">
          {t('QUICK_SEND_ENTRY_NEW_PRO_TIP_TEXT', {
            defaultValue: 'Save your parcel details as a product for faster shipping next time'
          })}
        </P2GText>
      </P2GAlert>
    </SidePanelSections>
  );
};

export default QuickSendEntryNew;
